import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "nav-blur bg-[#F9F8F6]/90 shadow-md py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("#home")}
          className="flex items-center gap-2.5 group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/nqwrkfli_LOGO2.jpeg"
            alt="Raihan International Logo"
            className="h-10 md:h-12 w-auto rounded-sm"
          />
          <span
            className={`text-xl md:text-2xl lg:text-3xl font-bold tracking-tight transition-colors ${
              scrolled ? "text-[#8D5A34]" : "text-white"
            }`}
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Raihan
          </span>
          <span
            className={`text-xl md:text-2xl lg:text-3xl font-light tracking-tight transition-colors ${
              scrolled ? "text-[#1A1614]" : "text-white/80"
            }`}
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            International
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8" data-testid="nav-desktop-links">
          {navLinks.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={() => scrollTo(link.href)}
              className={`text-sm font-medium tracking-wide transition-colors hover:text-[#8D5A34] ${
                scrolled ? "text-[#1A1614]" : "text-white/90"
              }`}
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {link.label}
            </button>
          ))}
          <Button
            data-testid="nav-inquiry-btn"
            onClick={() => scrollTo("#inquiry")}
            className="rounded-full px-6 py-2 bg-[#8D5A34] text-white hover:bg-[#5D3A20] transition-all hover:scale-105 text-sm"
          >
            Get a Quote
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden transition-colors ${scrolled ? "text-[#1A1614]" : "text-white"}`}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-[#F9F8F6] border-t border-[#8D5A34]/10 shadow-lg"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                onClick={() => scrollTo(link.href)}
                className="text-left text-[#1A1614] text-sm font-medium py-2 hover:text-[#8D5A34] transition-colors"
              >
                {link.label}
              </button>
            ))}
            <Button
              data-testid="nav-mobile-inquiry-btn"
              onClick={() => scrollTo("#inquiry")}
              className="rounded-full bg-[#8D5A34] text-white hover:bg-[#5D3A20] mt-2"
            >
              Get a Quote
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
